// vg-binding: P7.D-67
// vg-binding: P7.D-69
//
// apps/vendor/src/features/print-queue/OrderCard.tsx
//
// Phase 7 Wave 14 Task 54 — Kanban order card (draggable).
// UAT-12 (2026-05-16) — enrich with status pill + SLA chip + transition
// actions so vendors can drive the lifecycle without leaving the page.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, OrderStatusBadge } from '@printway/ui';

import { useTransitionItem, type OrderStatus } from '../orders/api.js';
import { TrackingInputModal } from '../orders/detail/TrackingInputModal.js';
import { STATUS_PILL_CLASS } from '../../lib/ui/statusPill.js';
import type { ItemLifecycleStatus, PrintQueueAssignment } from './api.js';

interface OrderCardProps {
  assignment: PrintQueueAssignment;
  onDragStart?: (assignmentId: string) => void;
  // R3-8 (2026-05-16) — parent opens detail drawer on click.
  onOpenDetail?: (assignment: PrintQueueAssignment) => void;
}

const STATUS_LABEL_KEY: Record<ItemLifecycleStatus, string> = {
  pending: 'statusLabel.pending',
  accepted: 'statusLabel.accepted',
  in_production: 'statusLabel.inProduction',
  quality_check: 'statusLabel.qualityCheck',
  ready_to_ship: 'statusLabel.readyToShip',
  shipped: 'statusLabel.shipped',
  completed: 'statusLabel.completed',
  cancelled: 'statusLabel.cancelled',
};

function slaTier(
  deadline: number | undefined,
  overdueLabel: string,
): {
  label: string;
  cls: string;
} | null {
  if (!deadline || deadline <= 0) return null;
  const diffH = Math.round((deadline - Date.now()) / (60 * 60 * 1000));
  if (diffH <= 0)
    return { label: overdueLabel, cls: STATUS_PILL_CLASS.danger };
  if (diffH < 12)
    return { label: `${diffH}h`, cls: STATUS_PILL_CLASS.warning };
  if (diffH < 36)
    return { label: `${diffH}h`, cls: STATUS_PILL_CLASS.progress };
  if (diffH < 24 * 7)
    return {
      label: `${Math.round(diffH / 24)}d`,
      cls: STATUS_PILL_CLASS.success,
    };
  return {
    label: `${Math.round(diffH / 24)}d`,
    cls: STATUS_PILL_CLASS.neutral,
  };
}

// Map current status → next legal transition for the in-card action.
function nextTransition(s: ItemLifecycleStatus | undefined):
  | { labelKey: string; to: OrderStatus }
  | null {
  switch (s) {
    // R5-5 — vendor accept newly-assigned items via the card too.
    case ('assigned' as unknown as ItemLifecycleStatus):
      return { labelKey: 'printQueue.card.transitionAccept', to: 'accepted' as OrderStatus };
    case 'accepted':
      return { labelKey: 'printQueue.card.transitionStart', to: 'in_production' };
    case 'in_production':
      return { labelKey: 'printQueue.card.transitionToQc', to: 'quality_check' };
    case 'quality_check':
      return { labelKey: 'printQueue.card.transitionReadyToShip', to: 'ready_to_ship' };
    case 'ready_to_ship':
      return { labelKey: 'printQueue.card.transitionShipped', to: 'shipped' };
    case 'shipped':
      return { labelKey: 'printQueue.card.transitionCompleted', to: 'completed' };
    // R4-1: explicit terminal states — no further transition. Returning null
    // hides the button. Stale local state (cache lagging behind real BE
    // state) is handled by the useTransitionItem onError → invalidate
    // hook so the card refreshes after a CAS-mismatch rejection.
    case 'completed':
    case 'cancelled':
    case 'pending':
      return null;
    default:
      return null;
  }
}

export function OrderCard({
  assignment,
  onDragStart,
  onOpenDetail,
}: OrderCardProps): JSX.Element {
  const { t } = useTranslation('orders');
  const a = assignment;
  const status = (a.item_status ?? 'pending') as ItemLifecycleStatus;
  const sla = slaTier(a.sla_deadline, t('list.overdue'));
  const next = nextTransition(status);
  const transitionM = useTransitionItem();
  const [err, setErr] = useState<string | null>(null);
  const [trackingModalOpen, setTrackingModalOpen] = useState(false);

  const canTransition =
    next !== null &&
    typeof a.order_id === 'string' &&
    typeof a.item_id === 'string' &&
    typeof a.item_version === 'number';

  // Shipping requires a tracking number (BE TRACKING_REQUIRED gate at
  // transitionService.ts:225). Surface the same TrackingInputModal that
  // the /orders detail ActionBar uses so the in-card button doesn't
  // immediately fail.
  const needsTrackingInput = next?.to === 'shipped';

  function runTransition(extra?: { tracking?: string }): void {
    if (!canTransition || !next) return;
    setErr(null);
    transitionM.mutate(
      {
        order_id: a.order_id!,
        item_id: a.item_id!,
        item_version: a.item_version!,
        to_state: next.to,
        ...(extra?.tracking ? { metadata: { tracking: extra.tracking } } : {}),
      },
      {
        onError: (e) => {
          setErr(e.userMessage ?? e.message ?? t('printQueue.card.errorTransition'));
        },
      },
    );
  }

  function handleTransition(): void {
    if (needsTrackingInput) {
      setTrackingModalOpen(true);
      return;
    }
    runTransition();
  }

  return (
    <div
      data-testid={`print-queue-order-card-${a.assignment_id}`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', a.assignment_id);
        onDragStart?.(a.assignment_id);
      }}
      className="cursor-grab rounded-lg border border-pw-border bg-pw-surface p-3 shadow-sm hover:bg-pw-bg active:cursor-grabbing"
    >
      <div className="flex items-center justify-between gap-2">
        {a.order_id ? (
          <Link
            to={`/orders/${a.order_id}/items/${a.item_id}`}
            onClick={(e) => e.stopPropagation()}
            className="font-mono text-xs text-pw-primary hover:underline"
          >
            #{a.order_id.slice(-8)}
          </Link>
        ) : (
          <span className="font-mono text-xs text-pw-text-muted">
            {a.assignment_id.slice(-8)}
          </span>
        )}
        {sla ? (
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${sla.cls}`}
          >
            SLA {sla.label}
          </span>
        ) : null}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <OrderStatusBadge
          status={status}
          label={t(STATUS_LABEL_KEY[status])}
          testId={`print-queue-order-card-status-${a.assignment_id}`}
        />
        {typeof a.quantity === 'number' && a.quantity > 0 ? (
          <span className="text-[11px] text-pw-text-muted">
            x{a.quantity}
          </span>
        ) : null}
      </div>
      <div className="mt-2 flex gap-1">
        {next && canTransition ? (
          <Button
            type="button"
            variant="outline"
            data-testid={`print-queue-order-card-next-${a.assignment_id}`}
            onClick={(e) => {
              e.stopPropagation();
              handleTransition();
            }}
            disabled={transitionM.isPending}
            className="h-auto flex-1 rounded-md border border-pw-border bg-pw-bg px-2 py-1 text-xs font-medium text-pw-text hover:bg-pw-surface disabled:cursor-not-allowed disabled:opacity-60"
          >
            {transitionM.isPending ? t('printQueue.card.transitionUpdating') : `→ ${t(next.labelKey)}`}
          </Button>
        ) : null}
        {onOpenDetail ? (
          <Button
            type="button"
            variant="outline"
            data-testid={`print-queue-order-card-detail-${a.assignment_id}`}
            onClick={(e) => {
              e.stopPropagation();
              onOpenDetail(a);
            }}
            className="h-auto rounded-md border border-pw-border bg-pw-bg px-2 py-1 text-xs font-medium text-pw-text hover:bg-pw-surface"
          >
            {t('printQueue.card.viewDetail')}
          </Button>
        ) : null}
      </div>
      {err ? (
        <p className="mt-1 text-[10px] text-red-600">{err}</p>
      ) : null}
      <TrackingInputModal
        open={trackingModalOpen}
        onCancel={() => setTrackingModalOpen(false)}
        onConfirm={async (tracking) => {
          setTrackingModalOpen(false);
          runTransition({ tracking });
        }}
      />
    </div>
  );
}

export default OrderCard;
